/**
 * AvatarOnboardingModal - AI-powered target audience & magnetic bio setup
 *
 * Two-step flow:
 * 1. User writes a free-form description of who they are and who they help
 * 2. AI generates a magnetic bio + structured avatar (audience, pains, desires, positioning, content ideas)
 *    User can review, edit, and save.
 *
 * Data is persisted to cai_profiles.preferences.avatarData and auto-loaded
 * for carousel generation.
 */

import React, { useState } from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { AvatarData, GeneratedAvatarResult } from '@/types';
import { saveProfile, ProfilePreferences } from '@/services/profileService';
import { getProfile } from '@/services/profileService';
import { generateMagneticBio } from '@/services/geminiService';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Loader2, X, Users, CheckCircle, Sparkles, ArrowLeft,
  Target, HeartCrack, Star, MessageSquareQuote, Compass, Lightbulb,
} from 'lucide-react';

interface AvatarOnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onAvatarSaved: (avatar: AvatarData) => void;
  required?: boolean;
  userId: string;
  initialData?: AvatarData;
}

type Step = 'input' | 'result';

const AvatarOnboardingModal: React.FC<AvatarOnboardingModalProps> = ({
  open,
  onClose,
  onAvatarSaved,
  required = false,
  userId,
  initialData,
}) => {
  const { t } = useTranslation();
  // --- Step state ---
  const [step, setStep] = useState<Step>(
    initialData?.magneticBio ? 'result' : 'input'
  );

  // --- Step 1: Input ---
  const [bioDescription, setBioDescription] = useState(
    initialData?.bioDescription || ''
  );
  const [isGenerating, setIsGenerating] = useState(false);

  // --- Step 2: Result (editable) ---
  const [magneticBio, setMagneticBio] = useState(initialData?.magneticBio || '');
  const [publicoAlvo, setPublicoAlvo] = useState(initialData?.publicoAlvo || '');
  const [dores, setDores] = useState<string[]>(initialData?.doresIdentificadas || []);
  const [desejos, setDesejos] = useState<string[]>(initialData?.desejosIdentificados || []);
  const [posicionamento, setPosicionamento] = useState(initialData?.posicionamento || '');
  const [metodo, setMetodo] = useState(initialData?.metodoAbordagem || '');
  const [temasConteudo, setTemasConteudo] = useState<string[]>(initialData?.temasConteudo || []);

  // --- Shared ---
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canGenerate = bioDescription.trim().length > 10;
  const canSave = magneticBio.trim().length > 10;

  // ---- Step 1: Generate with AI ----
  const handleGenerate = async () => {
    if (!canGenerate) return;
    setIsGenerating(true);
    setError(null);

    try {
      const result: GeneratedAvatarResult = await generateMagneticBio(bioDescription.trim());

      setMagneticBio(result.magneticBio || '');
      setPublicoAlvo(result.publicoAlvo || '');
      setDores(result.dores || []);
      setDesejos(result.desejos || []);
      setPosicionamento(result.posicionamento || '');
      setMetodo(result.metodo || '');
      setTemasConteudo(result.temasConteudo || []);
      setStep('result');
    } catch (err: any) {
      // Magnetic bio generation failed - error shown to user via setError
      setError(err.message || t('avatar.generateFailed'));
    } finally {
      setIsGenerating(false);
    }
  };

  // ---- Step 2: Save ----
  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setError(null);

    try {
      const currentProfile = await getProfile(userId);
      if (!currentProfile) {
        throw new Error(t('avatar.profileNotFound'));
      }

      const avatarData: AvatarData = {
        bioDescription: bioDescription.trim(),
        magneticBio: magneticBio.trim(),
        publicoAlvo: publicoAlvo.trim(),
        doresIdentificadas: dores.filter(d => d.trim()),
        desejosIdentificados: desejos.filter(d => d.trim()),
        posicionamento: posicionamento.trim(),
        metodoAbordagem: metodo.trim(),
        temasConteudo: temasConteudo.filter(t => t.trim()),
        // Legacy fields
        nicho: '',
        demografico: '',
        dores: '',
        desejos: '',
        crencas: '',
        objecoes: '',
        medos: '',
        momentoDeVida: '',
      };

      const currentPrefs: ProfilePreferences = currentProfile.preferences || {};
      await saveProfile(userId, currentProfile, {
        ...currentPrefs,
        avatarData,
      });

      onAvatarSaved(avatarData);
      onClose();
    } catch (err: any) {
      // Avatar save failed - error shown to user via setError
      setError(err.message || t('avatar.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Helpers for editable tag lists ----
  const removeTag = (list: string[], index: number, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(list.filter((_, i) => i !== index));
  };

  const addTag = (list: string[], setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) => {
    if (value.trim()) {
      setter([...list, value.trim()]);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl w-full max-w-2xl max-h-[92vh] shadow-2xl border border-border overflow-hidden flex flex-col">

        {/* Header */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {step === 'result' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setStep('input')}
                  className="h-8 w-8 mr-1"
                  title={t('avatar.backTitle')}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                {step === 'input' ? (
                  <Users className="h-5 w-5 text-primary" />
                ) : (
                  <Sparkles className="h-5 w-5 text-primary" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-bold">
                  {step === 'input' ? t('avatar.titleInput') : t('avatar.titleResult')}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {step === 'input'
                    ? (required ? t('avatar.subtitleInputRequired') : t('avatar.subtitleInputOptional'))
                    : t('avatar.subtitleResult')}
                </p>
              </div>
            </div>
            {!required && (
              <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1 min-h-0">

          {/* ======================== STEP 1: INPUT ======================== */}
          {step === 'input' && (
            <>
              <p className="text-sm text-muted-foreground">
                {t('avatar.description')}
              </p>

              <div className="bg-muted/50 rounded-lg p-3 border border-border">
                <p className="text-xs font-medium text-muted-foreground mb-1">{t('avatar.suggestedModelLabel')}</p>
                <p className="text-sm text-foreground italic">
                  "{t('avatar.suggestedModelText')}"
                </p>
              </div>

              <Textarea
                value={bioDescription}
                onChange={(e) => setBioDescription(e.target.value)}
                className="h-28 sm:h-36 resize-none text-sm"
                placeholder={t('avatar.inputPlaceholder')}
                disabled={isGenerating}
              />

              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">{t('avatar.examplesTitle')}</p>
                <div className="space-y-1.5">
                  {[t('avatar.example1'), t('avatar.example2'), t('avatar.example3')].map((example, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setBioDescription(example)}
                      disabled={isGenerating}
                      className={cn(
                        'w-full text-left text-xs p-2.5 rounded-lg border transition-colors',
                        bioDescription === example
                          ? 'bg-primary/10 border-primary/30 text-foreground'
                          : 'bg-muted/30 border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
                      )}
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>

              {isGenerating && (
                <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <Loader2 className="h-5 w-5 animate-spin text-primary flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{t('avatar.generating')}</p>
                    <p className="text-xs text-muted-foreground">{t('avatar.generatingHint')}</p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ======================== STEP 2: RESULT ======================== */}
          {step === 'result' && (
            <>
              {/* Magnetic Bio */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <label className="text-sm font-semibold">{t('avatar.magneticBio')}</label>
                </div>
                <Textarea
                  value={magneticBio}
                  onChange={(e) => setMagneticBio(e.target.value)}
                  className="h-24 resize-none text-sm"
                  placeholder={t('avatar.magneticBioPlaceholder')}
                />
              </div>

              {/* Structured fields in a grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Publico-Alvo */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Target className="h-3.5 w-3.5 text-blue-500" />
                    <label className="text-xs font-semibold">{t('avatar.publicTarget')}</label>
                  </div>
                  <Input
                    value={publicoAlvo}
                    onChange={(e) => setPublicoAlvo(e.target.value)}
                    className="h-8 text-xs"
                    placeholder={t('avatar.placeholderPublicTarget')}
                  />
                </div>

                {/* Metodo */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Compass className="h-3.5 w-3.5 text-green-500" />
                    <label className="text-xs font-semibold">{t('avatar.methodApproach')}</label>
                  </div>
                  <Input
                    value={metodo}
                    onChange={(e) => setMetodo(e.target.value)}
                    className="h-8 text-xs"
                    placeholder={t('avatar.placeholderMethod')}
                  />
                </div>
              </div>

              {/* Posicionamento */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <MessageSquareQuote className="h-3.5 w-3.5 text-purple-500" />
                  <label className="text-xs font-semibold">{t('avatar.positioning')}</label>
                </div>
                <Input
                  value={posicionamento}
                  onChange={(e) => setPosicionamento(e.target.value)}
                  className="h-8 text-xs"
                  placeholder={t('avatar.placeholderPositioning')}
                />
              </div>

              {/* Dores (tag list) */}
              <TagListEditor
                label={t('avatar.pains')}
                icon={<HeartCrack className="h-3.5 w-3.5 text-red-500" />}
                tags={dores}
                setTags={setDores}
                onRemove={(i) => removeTag(dores, i, setDores)}
                onAdd={(v) => addTag(dores, setDores, v)}
                placeholder={t('avatar.addPain')}
                tagColor="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20"
              />

              {/* Desejos (tag list) */}
              <TagListEditor
                label={t('avatar.desires')}
                icon={<Star className="h-3.5 w-3.5 text-amber-500" />}
                tags={desejos}
                setTags={setDesejos}
                onRemove={(i) => removeTag(desejos, i, setDesejos)}
                onAdd={(v) => addTag(desejos, setDesejos, v)}
                placeholder={t('avatar.addDesire')}
                tagColor="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20"
              />

              {/* Temas de Conteudo (tag list) */}
              <TagListEditor
                label={t('avatar.contentIdeas')}
                icon={<Lightbulb className="h-3.5 w-3.5 text-yellow-500" />}
                tags={temasConteudo}
                setTags={setTemasConteudo}
                onRemove={(i) => removeTag(temasConteudo, i, setTemasConteudo)}
                onAdd={(v) => addTag(temasConteudo, setTemasConteudo, v)}
                placeholder={t('avatar.addTopic')}
                tagColor="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20"
              />

              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                <p>{t('avatar.allFieldsEditable')}</p>
              </div>
            </>
          )}

          {/* Error */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-3 border-t border-border flex gap-3 flex-shrink-0">
          {step === 'input' && (
            <>
              {!required && (
                <Button
                  variant="secondary"
                  onClick={onClose}
                  disabled={isGenerating || isSaving}
                  className="flex-shrink-0"
                >
                  {t('common.cancel')}
                </Button>
              )}

              <div className="flex-1" />

              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !canGenerate}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('avatar.generatingButton')}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t('avatar.generateWithAI')}
                  </>
                )}
              </Button>
            </>
          )}

          {step === 'result' && (
            <>
              <Button
                variant="secondary"
                onClick={() => setStep('input')}
                disabled={isSaving}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('common.back')}
              </Button>

              <div className="flex-1" />

              <Button
                onClick={handleSave}
                disabled={isSaving || !canSave}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('avatar.saving')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {t('avatar.saveProfile')}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TAG LIST EDITOR (reusable sub-component)
// ============================================================================

interface TagListEditorProps {
  label: string;
  icon: React.ReactNode;
  tags: string[];
  setTags: React.Dispatch<React.SetStateAction<string[]>>;
  onRemove: (index: number) => void;
  onAdd: (value: string) => void;
  placeholder: string;
  tagColor: string;
}

const TagListEditor: React.FC<TagListEditorProps> = ({
  label, icon, tags, onRemove, onAdd, placeholder, tagColor,
}) => {
  const [newTag, setNewTag] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (newTag.trim()) {
        onAdd(newTag);
        setNewTag('');
      }
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        {icon}
        <label className="text-xs font-semibold">{label}</label>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag, i) => (
          <span
            key={i}
            className={cn('inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border', tagColor)}
          >
            {tag}
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="hover:text-destructive ml-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <Input
        value={newTag}
        onChange={(e) => setNewTag(e.target.value)}
        onKeyDown={handleKeyDown}
        className="h-7 text-xs"
        placeholder={placeholder}
      />
    </div>
  );
};

export default AvatarOnboardingModal;
